import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { EmergencyDisclaimer } from '../../components/EmergencyDisclaimer'
import { MatchBandBadge, EmptyState } from '../../components/ui'
import { phraseMatchReasons, BROAD_MATCH_EXPLANATION } from '../../lib/matchReasons'
import type { MatchResult } from '../../lib/types'

const LAST_INTAKE_KEY = 'atuune_last_intake_id'
const SAVED_MATCHES_KEY = 'atuune_saved_matches'
const PAGE_SIZE = 5

function getSavedMatchIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SAVED_MATCHES_KEY) || '[]')
  } catch {
    return []
  }
}

export default function PatientMatchesPage() {
  const navigate = useNavigate()
  const [results, setResults] = useState<MatchResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedIds, setSavedIds] = useState<string[]>(getSavedMatchIds())
  const intakeId = localStorage.getItem(LAST_INTAKE_KEY)

  async function loadPage(offset: number, append: boolean) {
    const data = await api(`/patients/me/matches?offset=${offset}&limit=${PAGE_SIZE}`)
    setTotal(data.total)
    setResults((prev) => (append ? [...prev, ...data.results] : data.results))
  }

  useEffect(() => {
    loadPage(0, false).finally(() => setLoading(false))
  }, [])

  async function showMore() {
    setBusy(true)
    try {
      await loadPage(results.length, true)
    } finally {
      setBusy(false)
    }
  }

  async function rerunMatch() {
    if (!intakeId) return
    setBusy(true)
    setError(null)
    try {
      await api('/patients/me/matches', { method: 'POST', body: JSON.stringify({ intake_id: intakeId }) })
      await loadPage(0, false)
    } catch {
      setError('Could not re-run matching.')
    } finally {
      setBusy(false)
    }
  }

  async function expandRadius() {
    if (!intakeId) return
    setBusy(true)
    setError(null)
    try {
      // No real geographic distance math exists yet — "expanding the radius"
      // means dropping the state constraint so the search widens to every
      // qualified provider regardless of state, instead of computing miles.
      await api(`/patients/me/intake/${intakeId}`, { method: 'PATCH', body: JSON.stringify({ logistics: { format: 'either' } }) })
      await api('/patients/me/matches', { method: 'POST', body: JSON.stringify({ intake_id: intakeId }) })
      await loadPage(0, false)
    } catch {
      setError('Could not expand your search.')
    } finally {
      setBusy(false)
    }
  }

  function toggleSaved(matchId: string) {
    const next = savedIds.includes(matchId) ? savedIds.filter((id) => id !== matchId) : [...savedIds, matchId]
    setSavedIds(next)
    localStorage.setItem(SAVED_MATCHES_KEY, JSON.stringify(next))
  }

  if (loading) return <div className="page-loading">Searching ATUUNE's provider network…</div>

  return (
    <div>
      <div className="page-head">
        <h1>Your matches</h1>
        <div className="ph-sub">{total} qualified provider{total === 1 ? '' : 's'} across ATUUNE's network</div>
      </div>
      <EmergencyDisclaimer />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn small outline" onClick={() => navigate('/patient/intake')} disabled={busy}>Update preferences</button>
        <button className="btn small outline" onClick={rerunMatch} disabled={busy || !intakeId}>Re-run match</button>
        <button className="btn small outline" onClick={expandRadius} disabled={busy || !intakeId}>Expand search radius</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      {results.length === 0 ? (
        <EmptyState>No providers passed the routing gates for this search yet. Try updating your preferences.</EmptyState>
      ) : (
        <div className="queue-list">
          {results.map((m) => {
            const reasons = phraseMatchReasons(m.match_reasons)
            const isSaved = savedIds.includes(m.id)
            return (
              <div key={m.id} className="card" style={{ marginBottom: 12, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{m.provider_name || 'A matched provider'}</div>
                    {(m.provider_city || m.provider_state_province) && (
                      <div className="muted" style={{ fontSize: 13 }}>
                        {[m.provider_city, m.provider_state_province].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, textAlign: 'right' }}>
                      Overall Match Quality
                    </div>
                    <MatchBandBadge band={m.match_band} />
                  </div>
                </div>

                {m.match_band === 'broad' && (
                  <p className="muted" style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>{BROAD_MATCH_EXPLANATION}</p>
                )}

                {reasons.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Why we matched you</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {reasons.map((r, i) => (
                        <li key={i} style={{ fontSize: 13.5 }}>✓ {r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button className="btn small outline" onClick={() => navigate(`/patient/providers/${m.provider_id}`)}>View Profile</button>
                  <button className="btn small" onClick={() => navigate(`/patient/book/${m.provider_id}?matchId=${m.id}`)}>Book Appointment</button>
                  <button className="btn small outline" onClick={() => toggleSaved(m.id)}>{isSaved ? 'Saved' : 'Save for Later'}</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {results.length < total && (
        <button className="btn outline full" style={{ marginTop: 12 }} onClick={showMore} disabled={busy}>
          {busy ? 'Loading…' : `Show more matches (${total - results.length} remaining)`}
        </button>
      )}
    </div>
  )
}
